import { z } from 'zod';
import { LABEL_COLORS, PRIORITIES, PROJECT_MEMBER_ROLES, PROJECT_STATUSES } from '../domain';
import { richTextSchema } from './rich-text';

const isoDate = z.iso
  .date()
  .nullable()
  .optional()
  .transform((v) => v ?? null);

export const projectStatusSchema = z.enum(PROJECT_STATUSES);
export const prioritySchema = z.enum(PRIORITIES);

export const projectInput = z
  .object({
    name: z.string().trim().min(1, 'Informe o nome do projeto').max(160),
    clientId: z
      .uuid()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    status: projectStatusSchema.default('planning'),
    priority: prioritySchema.default('medium'),
    color: z.enum(LABEL_COLORS).default('lavender'),
    startDate: isoDate,
    dueDate: isoDate,
    budgetCents: z
      .number()
      .int()
      .min(0)
      .max(1e13)
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    clientVisible: z.boolean().default(true),
    tagIds: z.array(z.uuid()).max(30).default([]),
  })
  .refine((v) => !v.startDate || !v.dueDate || v.startDate <= v.dueDate, {
    path: ['dueDate'],
    message: 'O prazo precisa ser depois do início',
  });
export type ProjectInput = z.input<typeof projectInput>;

export const projectContentInput = z.object({
  description: richTextSchema.nullable().optional(),
  briefing: richTextSchema.nullable().optional(),
});

export const tagDto = z.object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS) });
export type TagDto = z.infer<typeof tagDto>;
export const tagInput = z.object({
  name: z.string().trim().min(1).max(40),
  color: z.enum(LABEL_COLORS).default('sand'),
});

const person = z.object({ id: z.uuid(), name: z.string(), avatarUrl: z.string().nullable() });

export const projectMemberDto = person.extend({ role: z.enum(PROJECT_MEMBER_ROLES), orgRole: z.string() });
export type ProjectMemberDto = z.infer<typeof projectMemberDto>;

export const projectSummaryDto = z.object({
  id: z.uuid(),
  name: z.string(),
  status: projectStatusSchema,
  priority: prioritySchema,
  color: z.enum(LABEL_COLORS),
  coverUrl: z.string().nullable(),
  startDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  client: z
    .object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS), logoUrl: z.string().nullable() })
    .nullable(),
  members: z.array(projectMemberDto),
  tags: z.array(tagDto),
  /** Progresso pelas tarefas (Fase 4). */
  taskCount: z.number(),
  doneCount: z.number(),
  rank: z.string(),
  updatedAt: z.string(),
});
export type ProjectSummaryDto = z.infer<typeof projectSummaryDto>;

export const projectDto = projectSummaryDto.extend({
  description: richTextSchema.nullable(),
  briefing: richTextSchema.nullable(),
  /** null para quem não pode ver valores. */
  budgetCents: z.number().nullable(),
  clientVisible: z.boolean(),
  coverFileId: z.uuid().nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  /** O que a pessoa pode fazer neste projeto (para a interface; o servidor revalida). */
  can: z.object({
    update: z.boolean(),
    manageMembers: z.boolean(),
    delete: z.boolean(),
    createTasks: z.boolean(),
  }),
});
export type ProjectDto = z.infer<typeof projectDto>;

export const projectListQuery = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.union([projectStatusSchema, z.literal('open'), z.literal('all')]).optional(),
  clientId: z.uuid().optional(),
  mine: z.enum(['1']).optional(),
});

export const addProjectMemberInput = z.object({
  userId: z.uuid(),
  role: z.enum(PROJECT_MEMBER_ROLES).default('member'),
});
export const moveProjectInput = z.object({
  status: projectStatusSchema,
  beforeId: z.uuid().nullable().optional(),
  afterId: z.uuid().nullable().optional(),
});
